import React, { useState, useEffect } from 'react';
import { Camera, Upload, X, AlertTriangle, Barcode, ChevronDown, ChevronRight, Plus, Search } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';

const AddFoodPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    barcode: '',
    description: '',
    protein_per_serving: '',
    protein_per_100g: '',
    serving_size: '',
    contains_aspartame: false,
    ingredients: '',
    nutrition_facts: '',
  });

  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [isCreatingNewBrand, setIsCreatingNewBrand] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [locations, setLocations] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);

  // Calculate protein exchanges automatically - 1g protein = 1 exchange
  const proteinExchanges = formData.protein_per_serving 
    ? parseFloat(formData.protein_per_serving).toFixed(1)
    : 0;

  useEffect(() => {
    fetchBrands();
    fetchLocations();
    fetchTypes();
  }, []);

  const fetchBrands = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/brands`
        : 'https://api.pkutracker.securedeploy.uk/api/foods/brands';
      
      const response = await fetch(apiUrl);
      if (response.ok) {
        const data = await response.json();
        setBrands(data.brands || []);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/categories`
        : 'https://api.pkutracker.securedeploy.uk/api/foods/categories';
      
      const response = await fetch(apiUrl);
      if (response.ok) {
        const data = await response.json();
        setLocations(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchTypes = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/types`
        : 'https://api.pkutracker.securedeploy.uk/api/foods/types';
      
      const response = await fetch(apiUrl);
      if (response.ok) {
        const data = await response.json();
        setTypes(data.types || []);
      }
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
      
      // Hide validation warning if no more errors
      if (Object.keys(validationErrors).length === 1) {
        setShowValidationWarning(false);
      }
    }
  };

  const handleBrandInputChange = (e) => {
    const value = e.target.value;
    setBrandSearchQuery(value);
    setFormData(prev => ({ ...prev, brand: value }));
    
    if (value.trim()) {
      setShowBrandDropdown(true);
      // Show "Create new brand" option if the exact text doesn't exist
      // (even if there are partial matches)
      setIsCreatingNewBrand(!brands.some(brand => 
        brand.toLowerCase() === value.toLowerCase()
      ));
    } else {
      setShowBrandDropdown(false);
      setIsCreatingNewBrand(false);
    }
  };

  const selectBrand = (brand) => {
    setFormData(prev => ({ ...prev, brand }));
    setBrandSearchQuery(brand);
    setShowBrandDropdown(false);
    setIsCreatingNewBrand(false);
  };

  const handleLocationChange = (locationId) => {
    setSelectedLocationIds(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  const handleTypeChange = (typeId) => {
    setSelectedTypeIds(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(id => id !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
  };

  const createNewBrand = () => {
    if (brandSearchQuery.trim()) {
      setBrands(prev => [...prev, brandSearchQuery.trim()]);
      setFormData(prev => ({ ...prev, brand: brandSearchQuery.trim() }));
      setShowBrandDropdown(false);
      setIsCreatingNewBrand(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file)
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (photoId) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.id !== photoId);
    });
  };

  const handleBarcodeScan = (barcode) => {
    setFormData(prev => ({
      ...prev,
      barcode: barcode
    }));
    setShowBarcodeScanner(false);
  };

  const openBarcodeScanner = () => {
    setShowBarcodeScanner(true);
  };

  const checkForDuplicates = async () => {
    const duplicates = {};
    
    // Check for duplicate name + brand combination
    if (formData.name.trim() && formData.brand.trim()) {
      try {
        const apiUrl = import.meta.env.VITE_API_BASE_URL 
          ? `${import.meta.env.VITE_API_BASE_URL}/api/foods`
          : 'https://api.pkutracker.securedeploy.uk/api/foods';
        
        const response = await fetch(`${apiUrl}?search=${encodeURIComponent(formData.name.trim())}&brand=${encodeURIComponent(formData.brand.trim())}`);
        if (response.ok) {
          const result = await response.json();
          if (result.foods && result.foods.length > 0) {
            duplicates.name = `A food with the name "${formData.name.trim()}" and brand "${formData.brand.trim()}" already exists`;
          }
        }
      } catch (error) {
        console.error('Error checking for duplicate name:', error);
      }
    }
    
    // Check for duplicate barcode
    if (formData.barcode.trim()) {
      try {
        const apiUrl = import.meta.env.VITE_API_BASE_URL 
          ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/barcode/${encodeURIComponent(formData.barcode.trim())}`
          : `https://api.pkutracker.securedeploy.uk/api/foods/barcode/${encodeURIComponent(formData.barcode.trim())}`;
        
        const response = await fetch(apiUrl);
        if (response.ok) {
          duplicates.barcode = `A food with barcode "${formData.barcode.trim()}" already exists`;
        }
      } catch (error) {
        console.error('Error checking for duplicate barcode:', error);
      }
    }
    
    return duplicates;
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Food name is required';
    }
    
    if (!formData.protein_per_serving || parseFloat(formData.protein_per_serving) <= 0) {
      errors.protein_per_serving = 'Protein per serving is required and must be greater than 0';
    }
    
    if (!formData.serving_size || parseFloat(formData.serving_size) <= 0) {
      errors.serving_size = 'Serving size is required and must be greater than 0';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowValidationWarning(true);
      return;
    }
    
    // Check for duplicates
    const duplicates = await checkForDuplicates();
    if (Object.keys(duplicates).length > 0) {
      setValidationErrors(duplicates);
      setShowValidationWarning(true);
      return;
    }
    
    // Clear validation errors if form is valid
    setValidationErrors({});
    setShowValidationWarning(false);
    setIsSubmitting(true);

    try {
      console.log('Submitting food data:', formData);
      
      // Prepare the data for the API
      const foodData = {
        name: formData.name,
        brand: formData.brand || null,
        barcode: formData.barcode || null,
        description: formData.description || null,
        protein_exchanges: parseFloat(proteinExchanges),
        protein_per_100g: formData.protein_per_100g ? parseFloat(formData.protein_per_100g) : null,
        serving_size: formData.serving_size || null,
        contains_aspartame: formData.contains_aspartame,
        ingredients: formData.ingredients || null,
        nutrition_facts: formData.nutrition_facts || null,
        location_ids: selectedLocationIds.length > 0 ? selectedLocationIds : [],
        type_ids: selectedTypeIds.length > 0 ? selectedTypeIds : [],
      };
      
      console.log('🔍 Form data:', formData);
      console.log('🔍 Selected location IDs:', selectedLocationIds);
      console.log('🔍 Selected type IDs:', selectedTypeIds);
      console.log('🔍 Processed food data:', foodData);
      console.log('🔍 JSON stringified data:', JSON.stringify(foodData, null, 2));
      
      // Call the API
      console.log('🔍 Calling API with data:', foodData);
      console.log('🔍 API URL:', '/api/foods');
      console.log('🔍 Current origin:', window.location.origin);
      console.log('🔍 Full URL would be:', `${window.location.origin}/api/foods`);
      console.log('🔍 Vite dev server port:', window.location.port);
      
      // Use full API URL since proxy isn't working
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods`
        : 'https://api.pkutracker.securedeploy.uk/api/foods';
      console.log('🔍 Using API URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(foodData),
      });
      
      console.log('🔍 API Response status:', response.status);
      console.log('🔍 API Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          console.log('❌ API Error response:', errorData);
          console.log('❌ Error details:', errorData.error);
          
          // Handle different error formats
          if (errorData.error && typeof errorData.error === 'object') {
            errorMessage = errorData.error.message || errorData.error.details || JSON.stringify(errorData.error);
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          }
        } catch (e) {
          console.log('❌ Could not parse error response as JSON');
        }
        throw new Error(errorMessage);
      }
      
      // Check if response has content
      const responseText = await response.text();
      console.log('🔍 Raw API response:', responseText);
      
      if (!responseText) {
        throw new Error('API returned empty response');
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('✅ Food added successfully:', result);
      } catch (e) {
        console.error('❌ Failed to parse JSON response:', e);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}`);
      }
      
      // Success - reset form
      setFormData({
        name: '',
        brand: '',
        barcode: '',
        description: '',
        protein_per_serving: '',
        protein_per_100g: '',
        serving_size: '',
        contains_aspartame: false,
        ingredients: '',
        nutrition_facts: '',
      });
      setPhotos([]);
      setBrandSearchQuery('');
      
      alert('Food added successfully!');
    } catch (error) {
      console.error('Error adding food:', error);
      alert(`Error adding food: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.protein_per_serving && formData.serving_size;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-responsive py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Add New Food
          </h1>
          <p className="text-lg text-gray-600">
            Help the PKU community by adding information about a new food item. 
            Focus on the essential protein information - exchanges are calculated automatically.
          </p>
        </div>

        {/* Validation Warning */}
        {showValidationWarning && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Please fill in all required fields marked with *</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Essential Information */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">Essential Information</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="form-label">Food Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={`form-input ${validationErrors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="e.g., Apple, Bread, Yogurt"
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="form-label">Brand</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="brand"
                      value={brandSearchQuery}
                      onChange={handleBrandInputChange}
                      onFocus={() => setShowBrandDropdown(true)}
                      className={`form-input pr-10 ${validationErrors.brand ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Search or create brand..."
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                  
                  {/* Brand Dropdown */}
                  {showBrandDropdown && brandSearchQuery.trim() && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {/* Show existing brands that match */}
                      {brands
                        .filter(brand => brand.toLowerCase().includes(brandSearchQuery.toLowerCase()))
                        .map((brand) => (
                          <button
                            key={brand}
                            type="button"
                            onClick={() => selectBrand(brand)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                          >
                            {brand}
                          </button>
                        ))
                      }
                      
                      {/* Always show "Create new brand" option if the exact text doesn't exist */}
                      {isCreatingNewBrand && (
                        <button
                          type="button"
                          onClick={createNewBrand}
                          className="w-full text-left px-4 py-2 hover:bg-green-100 focus:bg-green-100 focus:outline-none text-green-700 font-medium border-t border-gray-200"
                        >
                          <Plus className="inline w-4 h-4 mr-2" />
                          Create "{brandSearchQuery}"
                        </button>
                      )}
                    </div>
                  )}
                  {validationErrors.brand && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.brand}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Barcode</label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      name="barcode"
                      value={formData.barcode}
                      onChange={handleInputChange}
                      className={`flex-1 form-input ${validationErrors.barcode ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="e.g., 1234567890123"
                    />
                    <button
                      type="button"
                      onClick={openBarcodeScanner}
                      className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                      title="Scan Barcode"
                    >
                      <Barcode className="h-5 w-5" />
                    </button>
                  </div>
                  {validationErrors.barcode && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.barcode}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="form-input"
                    placeholder="Brief description of the food item..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Protein Information */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">Protein Information</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="form-label">Protein per Serving (g) *</label>
                  <input
                    type="number"
                    name="protein_per_serving"
                    value={formData.protein_per_serving}
                    onChange={handleInputChange}
                    required
                    min="0"
                    max="50"
                    step="0.1"
                    className={`form-input ${validationErrors.protein_per_serving ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="e.g., 2.5"
                  />
                  {validationErrors.protein_per_serving && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.protein_per_serving}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Serving Size (g/ml) *</label>
                  <input
                    type="number"
                    name="serving_size"
                    value={formData.serving_size}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.1"
                    className={`form-input ${validationErrors.serving_size ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="e.g., 100"
                  />
                  {validationErrors.serving_size && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.serving_size}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Protein per 100g (g)</label>
                  <input
                    type="number"
                    name="protein_per_100g"
                    value={formData.protein_per_100g}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.1"
                    className="form-input"
                    placeholder="e.g., 2.5"
                  />
                </div>

                {/* Calculated Exchanges Display */}
                <div className="md:col-span-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-blue-900">Calculated Protein Exchanges</h3>
                        <p className="text-sm text-blue-700 mt-1">
                          Based on {formData.protein_per_serving || 0}g protein per {formData.serving_size || 0}g/ml serving
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-900">{proteinExchanges}</div>
                        <div className="text-sm text-blue-700">exchanges</div>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      1g protein = 1 exchange
                    </p>
                  </div>
                </div>

                <div className="md:col-span-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="contains_aspartame"
                      checked={formData.contains_aspartame}
                      onChange={handleInputChange}
                      className="rounded border-gray-300 text-warning-600 focus:ring-warning-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Contains Aspartame
                    </span>
                  </label>
                  {formData.contains_aspartame && (
                    <div className="mt-2 p-3 bg-warning-50 border border-warning-200 rounded-md">
                      <div className="flex items-center space-x-2 text-warning-800">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">Warning: This food contains aspartame</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Location and Type Selection */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">Categories & Organization</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Location Selection */}
                <div>
                  <label className="form-label">Storage Locations</label>
                  <p className="text-sm text-gray-600 mb-3">Where is this food typically stored?</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {locations.map((location) => (
                      <label key={location.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedLocationIds.includes(location.id)}
                          onChange={() => handleLocationChange(location.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div 
                          className="w-4 h-4 rounded border border-gray-300"
                          style={{ backgroundColor: location.color }}
                          title={location.color}
                        />
                        <span className="text-sm text-gray-700">{location.name}</span>
                      </label>
                    ))}
                    {locations.length === 0 && (
                      <p className="text-sm text-gray-500 italic">No locations available</p>
                    )}
                  </div>
                </div>

                {/* Type Selection */}
                <div>
                  <label className="form-label">Food Types</label>
                  <p className="text-sm text-gray-600 mb-3">What type of food is this?</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {types.map((type) => (
                      <label key={type.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedTypeIds.includes(type.id)}
                          onChange={() => handleTypeChange(type.id)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <div 
                          className="w-4 h-4 rounded border border-gray-300"
                          style={{ backgroundColor: type.color }}
                          title={type.color}
                        />
                        <span className="text-sm text-gray-700">{type.name}</span>
                      </label>
                    ))}
                    {types.length === 0 && (
                      <p className="text-sm text-gray-500 italic">No types available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information - Collapsible */}
          <div className="card">
            <button
              type="button"
              onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
              className="card-header cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between w-full">
                <h2 className="text-xl font-semibold text-gray-900">Additional Information</h2>
                {showAdditionalInfo ? (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                )}
              </div>
            </button>
            
            {showAdditionalInfo && (
              <div className="card-body">
                <div className="space-y-6">
                  <div>
                    <label className="form-label">Ingredients</label>
                    <textarea
                      name="ingredients"
                      value={formData.ingredients}
                      onChange={handleInputChange}
                      rows={4}
                      className="form-input"
                      placeholder="List of ingredients (optional)..."
                    />
                  </div>

                  <div>
                    <label className="form-label">Nutrition Facts</label>
                    <textarea
                      name="nutrition_facts"
                      value={formData.nutrition_facts}
                      onChange={handleInputChange}
                      rows={4}
                      className="form-input"
                      placeholder="Additional nutrition information (optional)..."
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Photo Upload */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-semibold text-gray-900">Photos</h2>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {/* Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <span className="btn btn-outline">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Photos
                    </span>
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Upload photos of the food item, packaging, or nutrition label
                  </p>
                </div>

                {/* Photo Preview */}
                {photos.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Uploaded Photos ({photos.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {photos.map((photo) => (
                        <div key={photo.id} className="relative">
                          <img
                            src={photo.preview}
                            alt="Food preview"
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(photo.id)}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              className="btn btn-outline btn-lg"
              onClick={() => window.history.back()}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="btn btn-primary btn-lg"
            >
              {isSubmitting ? 'Adding Food...' : 'Add Food'}
            </button>
          </div>
        </form>
      </div>

      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={showBarcodeScanner}
        onScan={handleBarcodeScan}
        onClose={() => setShowBarcodeScanner(false)}
      />
    </div>
  );
};

export default AddFoodPage;
